import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { AngularContext } from 'react-app';

import { useSelector } from 'react-redux';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { Institution } from 'redux/schemas/models/institution';
import { useAppDispatch } from 'redux/store';
import { enableAccredibleSettings } from 'redux/actions/digital-credential';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { AccredibleSettingsPayload } from 'redux/schemas/models/credly';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvTextInput from 'shared/components/inputs/nv-text-input';

import {
  doubleSpacing,
  tripleSpacing,
  quarterSpacing,
  cardIconSize,
} from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';

type Props = {
  show: boolean;
  onClose: () => void;
};

const styles = css`
  .message {
    margin-bottom: ${5 * quarterSpacing}px;
  }
`;

const modalStyles = css`
  .bs4-modal-body {
    padding: ${doubleSpacing}px ${tripleSpacing}px ${cardIconSize}px ${tripleSpacing}px;
  }
`;

const AccredibleSettings = ({ show, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const institution: Institution = useSelector(state => getCurrentInstitution(state));
  const { injectServices } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const hasAccredibleIntegration = institution?.isAccredibleEnabled ?? false;
  const existingApiToken = institution?.accredibleConfig?.apiKey ?? '';
  const [apiToken, setApiToken] = useState(existingApiToken);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setApiToken(existingApiToken);
    }
  }, [show, existingApiToken]);

  const handleClose = () => {
    onClose();
  };

  const handleEnable = () => {
    setIsSaving(true);

    wrapThunkAction<AccredibleSettingsPayload>(
      dispatch(
        enableAccredibleSettings({
          institutionId: institution.id,
          apiKey: apiToken,
        }),
      ),
    )
      .then(response => {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: hasAccredibleIntegration
              ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.UPDATE_MESSAGE()
              : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.SUCCESS_MESSAGE(),
          }),
        );

        InstitutionsManager.institution.updateFromReact({
          ...response.payload.institutionList,
        });

        handleClose();
      })
      .catch(() => {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.SETTINGS_MODAL.ERROR_TITLE(),
            message: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.SETTINGS_MODAL.ERROR_MESSAGE(),
          }),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const getButtonText = () => {
    if (isSaving) {
      return hasAccredibleIntegration
        ? t.FORM.UPDATING()
        : t.FORM.ENABLING();
    }
    return hasAccredibleIntegration
      ? t.FORM.UPDATE()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.ENABLE_ACCREDIBLE_INTEGRATION();
  };

  return (
    <React.Fragment>
      <NvModal
        show={show}
        onClose={handleClose}
        type={ModalType.FIXED}
        height='auto'
        modalStyles={modalStyles}
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.TITLE()}
        data-qa={config.pendo.digitalCredential.accredibleIntegration.modal}
        body={(
          <div css={styles}>
            <div className='message text-body'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DESCRIPTION()}
            </div>

            <NvTextInput
              required
              autoComplete='off'
              name='apiToken'
              withLabel
              labelAlwaysShown
              labelClassName='text-gray-3'
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              label={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.API_TOKEN()}
              aria-label={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.API_TOKEN()}
              placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.API_TOKEN()}
              data-qa={config.pendo.digitalCredential.accredibleIntegration.apiToken}
            />

            <div className='mt-8 mb-6 d-flex justify-content-center'>
              <Button
                variant='secondary'
                onClick={handleClose}
                data-qa={config.pendo.digitalCredential.accredibleIntegration.cancel}
                className='mr-2'
              >
                {t.FORM.CANCEL()}
              </Button>

              <Button
                variant='primary'
                disabled={!apiToken.trim() || isSaving}
                onClick={handleEnable}
                data-qa={!hasAccredibleIntegration
                  ? config.pendo.digitalCredential.accredibleIntegration.enable
                  : config.pendo.digitalCredential.accredibleIntegration.update}
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        )}
      />
    </React.Fragment>
  );
};

export default AccredibleSettings;
